/**
 * COIL PRINT SHEET — React port of `coil.selector.print.tmpl` (1,522 lines).
 *
 * Legacy `print()` (coil.selector.js:4066-4211) compiles that Handlebars
 * template, drops the HTML into a `div.print-content` appended to `<body>`,
 * runs `validateOutputs()` over it (which formats every `.number_output`), sets
 * `document.title`, and calls `window.print()` 200 ms later.
 *
 * That matters because `common.css`'s print block hides the app entirely —
 *   `@media print { body > *:not([class*=print]) { display: none !important } }`
 * (common.css:22138) — so with no `.print-content` on the page, Print produces
 * BLANK paper. The React port previously called `window.print()` on the live
 * dialog, which is exactly that case.
 *
 * The sheet is styled by the compiled `rating_mode_print.scss` rules, all of
 * them inside `@media print` and scoped under `.coil-rating-mode`
 * (common.css:22572+). The stylesheet keys on `td:nth-of-type(N)`, so the
 * NUMBER and ORDER of `<td>`s in every row is load-bearing — including the
 * empty ones. Rows are transcribed 1:1 from the template; the `tmpl:NNN`
 * comments are the line in `coil.selector.print.tmpl` each block came from.
 *
 * Formatting: legacy's `validateOutputs` only touches `.number_output`
 * (decimal → toFixed(2)+commas, else round+commas). Spans carrying only
 * `data-format="number"` (the capacity cells) are NOT touched by it — their
 * values arrive pre-formatted from the rating response. We format up-front with
 * the same helpers the dialog uses, which is the established port convention.
 */

import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import { coilOptionId, coilOptionLabel } from './coilSelectorData';
import { CoilPrintDrawing } from './CoilPrintDrawing';

// ---------------------------------------------------------------------------
// leaf renderers
// ---------------------------------------------------------------------------

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="label">{children}</span>
);

const Unit = ({ children }: { children: React.ReactNode }) => (
  <span className="unit">{children}</span>
);

/** `<span class="unit temp">F</span>` — the CSS renders the degree ring. */
const TempUnit = () => <span className="unit temp">F</span>;

const Sel = ({ children }: { children?: React.ReactNode }) => (
  <span className="print-selection">{children}</span>
);

/** `.number_output[data-format=decimal]` */
const Dec = ({ v }: { v: unknown }) => (
  <span className="number_output" data-format="decimal">
    {formatDecimalDisplay(v)}
  </span>
);

/** `.number_output[data-format=integer]` */
const Int = ({ v }: { v: unknown }) => (
  <span className="number_output" data-format="integer">
    {formatIntegerDisplay(v)}
  </span>
);

/** `data-format="number"` WITHOUT `.number_output` — the capacity cells. */
const Numb = ({ v, className }: { v: unknown; className?: string }) => (
  <span className={className} data-format="number">
    {formatIntegerDisplay(v)}
  </span>
);

const Empty = () => <td />;

// ---------------------------------------------------------------------------

export interface CoilPrintSheetProps {
  /** user.report_color, the raw legacy "(r,g,b)" string used as `rgb${…}`. */
  reportColor?: string | null;
  /** project.report_co — the company line above the project block. */
  reportCo?: string | null;
  /** project.report_logo — dropped into `.mfg-img` (legacy appends an <img>). */
  reportLogo?: string | null;
  year: number;
  projectName?: string | null;
  projectNumber?: string | null;
  elevation?: unknown;
  ahuTag?: string | null;
  sectionTag?: string | null;
  /** params_coil.rating_datetime — legacy `YYYY.MM.DD_HH:mm:ss`. */
  ratingDatetime?: string | null;
  /** header host: no project / AHU / section, and `Project No.` is omitted. */
  headerMode?: boolean;

  coilType: string;
  /** duty VALUE ('cooling' / 'heating' / …), not the label. */
  duty: string;
  /** conn-hand VALUE ('right' / 'left'). */
  hand: string;
  /** evaporator style VALUE ('std' / dual). */
  style: string;

  /** the flat params_coil + model state (CoilSelector `state.current`). */
  s: Record<string, any>;
  /** the rated row + the derived per/all totals (CoilSelector `perf`). */
  perf: Record<string, any>;
  /** the id→label master lists (CoilSelector `masterLists`). */
  masterLists: Record<string, any[]>;
  /** type / duty / htfluid rows, which live outside masterLists on the board. */
  typeRows: any[];
  dutyRows: any[];
  htfluidRows: any[];
  /** params_coil.coil_mfg — the manufacturer NAME. */
  mfgName?: string | null;
  /** header_side + return_side flange widths + fl (legacy `casing_length`). */
  casingLength: unknown;
  /** output_data_print.circuiting_rating_output, with the legacy fallback. */
  circuiting?: string | null;
  faceVelocityActual?: unknown;

  warnings: string[];
  cmsNotes: string[];
  apiNotes: string[];
  ratedNotes: string[];
}

export const CoilPrintSheet = (p: CoilPrintSheetProps) => {
  const { s, perf, masterLists, coilType, duty, headerMode } = p;
  const cooling = duty === 'cooling';
  const fluid = coilType === 'fluid';
  const evaporator = coilType === 'evaporator';
  const steam = coilType === 'steam';

  /** `{{#each collections.X}}{{#ifCompare item.id '==' ../params_coil.Y}}` —
   * the template's id→text lookup, using the same key map the selects use. */
  const lbl = (field: string, listKey?: string, value?: unknown): string => {
    const raw = value === undefined ? s[field] : value;
    if (raw == null || raw === '') return '';
    const rows = masterLists[listKey ?? field] ?? [];
    const row = rows.find((r) => String(coilOptionId(r, field)) === String(raw));
    return row ? coilOptionLabel(row, field) : '';
  };
  const flange = (field: string) => lbl(field, 'casing_flange_widths');
  /** the rated value when the rate returned one, else the saved id's own value. */
  const orLbl = (v: unknown, field: string) => (v == null || v === '' ? lbl(field) : v);
  const flat = (rows: any[], id: unknown): string => {
    if (id == null || id === '') return '';
    const row = (rows ?? []).find((r: any) => String(r?.item?.id ?? r?.id) === String(id));
    const o = row?.item ?? row;
    return o ? String(o.name ?? o.value ?? '') : '';
  };

  const typeName = flat(p.typeRows, s.type_id);
  const dutyName = flat(p.dutyRows, s.duty_id);
  const fluidName = flat(p.htfluidRows, s.htfluid_id);
  // tmpl:112-120 — the glycol % rides the fluid name for a NON-water fluid.
  const showGlycol = fluid && fluidName !== '' && fluidName.toLowerCase() !== 'water';

  return (
    <>
      {/* tmpl:6-17 — the sheet's OWN @page (A4 + margins) overriding the app's
          `size: letter portrait`, and the footer nudge. jQuery keeps this
          <style> when it drops the compiled template into `.print-content`. */}
      <style>
        {'@media print{@page{size:A4;margin-top:10mm;margin-bottom:15mm}' +
          '.table-footer{bottom:-10mm !important}}'}
      </style>
      <header className="print-hide" />
      <div className="coil-rating-mode">
        {/* tmpl:22-37 — running header. */}
        <table>
          <tbody>
            <tr>
              <td>
                <div
                  className="container-header clearfix"
                  style={{ borderTop: `4px solid rgb${p.reportColor ?? ''}` }}
                >
                  <h3>
                    <span className="project-ahu-names">
                      <span className="report-name">Engineering : Coil Selection</span>
                      <span className="project-name">
                        <span>{p.projectName ?? ''}</span> :{' '}
                        <span className="ahu-name">{p.ahuTag ?? ''}</span>
                      </span>
                    </span>
                    <span className="time-stamp">Rated on: {p.ratingDatetime ?? ''}</span>
                  </h3>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* tmpl:38-69 — logo + project block. */}
        <table className="header-info coil-header">
          <tbody>
            <tr>
              <td>
                <div className="mfg-img">
                  {p.reportLogo ? (
                    <img src={p.reportLogo} tabIndex={-1} className="print-logo" alt="" />
                  ) : null}
                </div>
              </td>
              <td>
                <div className="project-info">
                  <span className="left">{p.reportCo ?? ''}</span>
                  <ul>
                    <li>
                      <span className="label">Project Name</span>
                      <span>{p.projectName ?? ''}</span>
                    </li>
                    {/* `{{#unless header}}` — the header host has no project. */}
                    {!headerMode && (
                      <li>
                        <span className="label">Project No.</span>
                        <span>{p.projectNumber ?? ''}</span>
                      </li>
                    )}
                    <li>
                      <span className="label">AHU</span>
                      <span>{p.ahuTag ?? ''}</span>
                    </li>
                    <li>
                      <span className="label">Section</span>
                      <span>{p.sectionTag ? p.sectionTag : 'Coil'}</span>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* tmpl:70-543 — the main grid. */}
        <table className={`coil-table ${coilType}`}>
          <tbody>
            {/* tmpl:71-78 */}
            <tr>
              <td><Label>Manufacturer</Label></td>
              <Empty />
              <td><Sel>{p.mfgName ?? ''}</Sel></td>
              <td><Label>Altitude</Label></td>
              <td><Unit>ft</Unit></td>
              <td><Sel>{p.elevation as React.ReactNode}</Sel></td>
            </tr>
            {/* tmpl:79-86 */}
            <tr>
              <td><Label>Model</Label></td>
              <Empty />
              <td><Sel>{perf.model ?? ''}</Sel></td>
              <Empty />
              <Empty />
              <Empty />
            </tr>
            {/* tmpl:87-105 — the Per Coil / Total All Coils captions live here. */}
            <tr>
              <td><Label>Type / Duty</Label></td>
              <Empty />
              <td>
                <Sel>
                  <span>{typeName}</span>
                </Sel>{' '}
                /{' '}
                <Sel>
                  <span>{dutyName}</span>
                </Sel>
              </td>
              <Empty />
              <Empty />
              <td><span className="">Per Coil</span></td>
              <td><span className="">Total All Coils</span></td>
            </tr>
            {/* tmpl:106-129 */}
            <tr>
              <td><Label>HT Fluid</Label></td>
              <Empty />
              <td>
                <Sel>
                  <span>
                    <span>{fluidName}</span>
                  </span>
                  {showGlycol && (
                    <>
                      <span> {s.glycol_percent as React.ReactNode}</span>
                      <span className="unit">%</span>
                    </>
                  )}
                </Sel>
              </td>
              <td>
                <Label>
                  {Number(p.elevation) === 0 ? 'Airflow Std' : 'Airflow Actual'}
                </Label>
              </td>
              <td><Unit>CFM</Unit></td>
              <td><Numb v={s.airflow_actual} className="print-selection" /></td>
              <td><Numb v={s.airflow_actual_total} className="print-selection" /></td>
            </tr>
            {/* tmpl:130-149 */}
            <tr>
              <td><Label>Qty FH x FL</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                <Sel>
                  <span>({s.qty_stacked as React.ReactNode}) </span>
                  <span>{lbl('fh')}</span> x <span>{lbl('fl')}</span>
                </Sel>
              </td>
              <td><Label>Face Area</Label></td>
              <td>
                <span className="unit">
                  ft<span className="sup">2</span>
                </span>
              </td>
              <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.face_area)}</span></td>
              <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.face_area_total)}</span></td>
            </tr>
            {/* tmpl:150-162 */}
            <tr>
              <td><Label>Rows / FPI</Label></td>
              <Empty />
              <td>
                <Sel>
                  {/* A FRESH rate returns rows/fpi as values; a STORED row
                      carries only the ids, which legacy's setOutputs resolves
                      off the master lists (getParamValueById, js:2039-2046). */}
                  <Int v={orLbl(perf.rows, 'rows_id')} /> /{' '}
                  <Dec v={orLbl(perf.fpi, 'fpi_id')} />
                </Sel>
              </td>
              <td><Label>Face Velocity</Label></td>
              <td><Unit>fpm</Unit></td>
              <td>
                <span className="print-selection number_output" data-format="integer">
                  {formatIntegerDisplay(
                    Number(s.mfg_id) === 27 ? p.faceVelocityActual : perf.face_velocity_std
                  )}
                </span>
              </td>
            </tr>
            {/* tmpl:163-183 — steam has no circuiting; the 3 cells stay empty. */}
            <tr>
              {!steam ? (
                <>
                  <td><Label>Circuiting</Label></td>
                  <Empty />
                  <td>
                    <Sel>{p.circuiting ?? ''}</Sel>
                    {String(perf.feeds ?? '') !== '0' && perf.feeds ? (
                      <> / {perf.feeds} feeds </>
                    ) : null}
                  </td>
                </>
              ) : (
                <>
                  <Empty />
                  <Empty />
                  <Empty />
                </>
              )}
              <td><Label>Air Pressure Drop</Label></td>
              <td><Unit>in WC</Unit></td>
              <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.apd)}</span></td>
            </tr>
            {/* tmpl:184-195 */}
            {evaporator && (
              <tr className="evaporator-tr">
                <td><Label>Style</Label></td>
                <Empty />
                <td><Sel>{p.style}</Sel></td>
                <Empty />
                <Empty />
                <Empty />
              </tr>
            )}
            {/* tmpl:196-220 */}
            <tr>
              <td><Label>Tube Dia / Thickness</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                <Sel>
                  <span>{lbl('tube_dia_id')}</span>
                  <span>{lbl('tube_wall_thick_id') ? ` / ${lbl('tube_wall_thick_id')}` : ''}</span>
                </Sel>
              </td>
              <td><Label>EADB {cooling ? '/ EAWB' : ''}</Label></td>
              <td><TempUnit /></td>
              <td>
                <Sel>
                  <Dec v={s.eadb_temp} />
                  {cooling && (
                    <>
                      {' / '}
                      <Dec v={s.eawb_temp} />
                    </>
                  )}
                </Sel>
              </td>
            </tr>
            {/* tmpl:221-246 */}
            <tr>
              <td><Label>Tube Mtl {fluid ? '| Interior' : ''}</Label></td>
              <Empty />
              <td>
                <Sel>
                  <span>{lbl('tube_mtl_id')}</span>
                  {fluid && (
                    <>
                      {' | '}
                      <span>{lbl('tube_interior_id')}</span>
                    </>
                  )}
                </Sel>
              </td>
              <td><Label>LADB {cooling ? '/ LAWB' : ''}</Label></td>
              <td><TempUnit /></td>
              <td>
                <Sel>
                  <Dec v={perf.ladb_temp} />
                  {cooling && (
                    <>
                      {' / '}
                      <Dec v={perf.lawb_temp} />
                    </>
                  )}
                </Sel>
              </td>
            </tr>
            {/* tmpl:247-292 — cooling shows Sensible here, heating shows Total. */}
            <tr>
              <td><Label>Tube Spacing V x H</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                <Sel>
                  <span>{lbl('tube_vert_spacing_id')}</span> x{' '}
                  <span>{lbl('tube_hor_spacing_id')}</span>
                </Sel>
              </td>
              {cooling ? (
                <>
                  <td><Label>Capacity Sensible</Label></td>
                  <td><Unit>btu/hr</Unit></td>
                  <td><Sel><Numb v={perf.capacity_sensible} /></Sel></td>
                  <td className="capacity_sensible_total_int">
                    <Sel><Numb v={perf.capacity_sensible_total_int} /></Sel>
                  </td>
                </>
              ) : (
                <>
                  <td><Label>Capacity Total</Label></td>
                  <td><Unit>btu/hr</Unit></td>
                  <td><Sel><Numb v={perf.capacity_total} /></Sel></td>
                  <td className="capacity_sensible_total_int">
                    <Sel><Numb v={perf.capacity_total_total} className="ll" /></Sel>
                  </td>
                </>
              )}
            </tr>
            {/* tmpl:293-317 — fluid only. */}
            {fluid && (
              <tr>
                <td><Label>Tube Cleanable</Label></td>
                <Empty />
                <td>{lbl('tube_cleanable_id') ? <Sel>{lbl('tube_cleanable_id')}</Sel> : <Sel>--</Sel>}</td>
                {cooling && (
                  <>
                    <td><Label>Capacity Total</Label></td>
                    <td><Unit>btu/hr</Unit></td>
                    <td><Sel><Numb v={perf.capacity_total} /></Sel></td>
                    <td className="capacity_sensible_total_int">
                      <Sel><Numb v={perf.capacity_total_total} className="ll" /></Sel>
                    </td>
                  </>
                )}
              </tr>
            )}
            {/* tmpl:318-356 */}
            <tr>
              <td><Label>Fins</Label></td>
              <Empty />
              <td>
                <Sel>
                  <span>{lbl('fin_mtl_id')}</span>
                  <span>{lbl('fin_pattern_id') ? ` / ${lbl('fin_pattern_id')}` : ''}</span>
                </Sel>
              </td>
              {fluid ? (
                <>
                  <td><Label>EFT / LFT</Label></td>
                  <td><TempUnit /></td>
                  <td>
                    <Sel>
                      <Dec v={s.entering_fluid_temp} /> / <Dec v={perf.leaving_fluid_temp} />
                    </Sel>
                  </td>
                </>
              ) : (
                cooling && (
                  <>
                    <td><Label>Capacity Total</Label></td>
                    <td><Unit>btu/hr</Unit></td>
                    <td><Sel><Numb v={perf.capacity_total} /></Sel></td>
                    <td className="capacity_sensible_total_int">
                      <Sel><Numb v={perf.capacity_total_total} className="ll" /></Sel>
                    </td>
                  </>
                )
              )}
            </tr>
            {/* tmpl:357-381 */}
            <tr>
              <td><Label>Fin Thickness</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                {/* `params_coil.fin_thick` is the resolved VALUE; the port only
                    materialises it at rate time (collectRateState), so fall
                    back to the fin-thickness option's own value. */}
                <Sel>
                  <span>
                    {s.fin_thick != null && s.fin_thick !== '' ? (
                      <span>{s.fin_thick}</span>
                    ) : lbl('fin_thick_id') ? (
                      <span>{lbl('fin_thick_id')}</span>
                    ) : null}
                  </span>
                </Sel>
              </td>
              {fluid && (
                <>
                  <td><Label>Fluid Flow</Label></td>
                  <td><Unit>gpm</Unit></td>
                  <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.fluid_flow)}</span></td>
                  <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.fluid_flow_total)}</span></td>
                </>
              )}
              {evaporator && (
                <>
                  <td><Label>Evaporator Mode</Label></td>
                  <Empty />
                  <td><Sel>{lbl('evaporator_mode_id')}</Sel></td>
                </>
              )}
              {steam && (
                <>
                  <td><Label>Entering Superheat</Label></td>
                  <td><TempUnit /></td>
                  <td><Sel>{s.entering_superheat as React.ReactNode}</Sel></td>
                </>
              )}
            </tr>
            {/* tmpl:382-413 */}
            <tr>
              <td><Label>Casing Material</Label></td>
              <Empty />
              <td><Sel><span>{lbl('casing_mtl_id')}</span></Sel></td>
              {fluid && (
                <>
                  <td><Label>Fluid Velocity</Label></td>
                  <td><Unit>fps</Unit></td>
                  <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.fluid_velocity)}</span></td>
                </>
              )}
              {evaporator && (
                <>
                  <td><Label>Refrigerant Charge</Label></td>
                  <td><Unit>lb</Unit></td>
                  <td><Sel>{formatDecimalDisplay(perf.refrigerant_charge)}</Sel></td>
                </>
              )}
              {steam && (
                <>
                  <td><Label>Steam Pressure</Label></td>
                  <td><Unit>PSI</Unit></td>
                  <td><Sel>{s.steam_pressure as React.ReactNode}</Sel></td>
                </>
              )}
            </tr>
            {/* tmpl:414-439 */}
            <tr>
              <td><Label>Casing Type</Label></td>
              <Empty />
              <td><Sel><span>{lbl('casing_type_id')}</span></Sel></td>
              {fluid && (
                <>
                  <td><Label>Fluid Pressure Drop</Label></td>
                  <td><Unit>ft WC</Unit></td>
                  <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.fluid_pd)}</span></td>
                </>
              )}
              {evaporator && (
                <>
                  <td><Label>SST | LRT</Label></td>
                  <td><TempUnit /></td>
                  <td>
                    <Sel>
                      {/* Handlebars `#if` is falsy-based, so 0 also falls to the
                          default — reproduced (tmpl:429-431). */}
                      <span>{s.suction_temp ? formatDecimalDisplay(s.suction_temp) : '45'}</span> |{' '}
                      <span>{s.ref_liquid_temp ? formatDecimalDisplay(s.ref_liquid_temp) : '110'}</span>
                    </Sel>
                  </td>
                </>
              )}
              {steam && (
                <>
                  <td><Label>Condensate per circuit</Label></td>
                  <td><Unit>lb/hr</Unit></td>
                  <td><span className="print-selection number_output" data-format="decimal">{formatDecimalDisplay(perf.condensate_load_per_circuit)}</span></td>
                </>
              )}
            </tr>
            {/* tmpl:440-479 */}
            <tr>
              <td><Label>Casing H / R / T / B</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                <Sel>
                  <span>{flange('casing_flange_header_side_id')}</span> /{' '}
                  <span>{flange('casing_flange_return_side_id')}</span> /{' '}
                  <span>{flange('casing_flange_top_id')}</span> /{' '}
                  <span>{flange('casing_flange_btm_id')}</span>
                </Sel>
              </td>
              {fluid && (
                <>
                  <td><Label>Reynold&apos;s Number</Label></td>
                  <Empty />
                  <td><Sel>{perf.reynolds_number as React.ReactNode}</Sel></td>
                </>
              )}
              {evaporator && (
                <>
                  <td><Label>Tube Vapor Velocity</Label></td>
                  <td><Unit>fps</Unit></td>
                  <td>{perf.tube_vapor_velocity ? String(perf.tube_vapor_velocity) : ''}</td>
                </>
              )}
              {steam && (
                <>
                  <td><Label>Condensate Conn Dia</Label></td>
                  <td><Unit>in</Unit></td>
                  <td><Sel>{s.condensate_conn_size_id as React.ReactNode}</Sel></td>
                </>
              )}
            </tr>
            {/* tmpl:480-510 */}
            {s.casing_stacking_flange ? (
              <tr>
                <td><Label>Stacking Flanges</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
                <td><Label>Fouling In/Out</Label></td>
                <td>
                  <span className="unit">
                    hr.ft<span className="sup">2</span>
                    <span className="unit temp">F/btu</span>
                  </span>
                </td>
                <td>
                  <Sel>
                    <Dec v={s.fouling_factor_inside} /> / <Dec v={s.fouling_factor_outside} />
                  </Sel>
                </td>
              </tr>
            ) : s.fouling_factor_inside ? (
              <tr>
                <Empty />
                <Empty />
                <Empty />
                <td><Label>Fouling Factor In / Out</Label></td>
                <td>
                  <span className="unit">
                    hr.ft<span className="sup">2</span>
                    <span className="unit temp">F/btu</span>
                  </span>
                </td>
                <td>
                  <Sel>
                    <Dec v={s.fouling_factor_inside} /> / <Dec v={s.fouling_factor_outside} />
                  </Sel>
                </td>
              </tr>
            ) : null}
            {/* tmpl:512-523 */}
            <tr>
              <td><Label>Fastening method</Label></td>
              <Empty />
              <td><Sel><span>{lbl('fastening_method_id')}</span></Sel></td>
              {evaporator && (
                <>
                  <td><Label>Refrigerant Pressure Drop</Label></td>
                  <td><Unit>PSI</Unit></td>
                  <td><Sel>{perf.fluid_pd as React.ReactNode}</Sel></td>
                </>
              )}
            </tr>
            {/* tmpl:525-542 */}
            <tr>
              {s.mounting_holes ? (
                <>
                  <td><Label>Mounting Holes</Label></td>
                  <Empty />
                  <td><Sel>true</Sel></td>
                </>
              ) : evaporator ? (
                <>
                  <Empty />
                  <Empty />
                  <Empty />
                </>
              ) : null}
              {evaporator && (
                <>
                  <td><Label>Superheat</Label></td>
                  <td><TempUnit /></td>
                  <td><Sel>{s.entering_superheat as React.ReactNode}</Sel></td>
                </>
              )}
            </tr>
          </tbody>
        </table>

        {/* tmpl:544-693 — the connection block. */}
        <table className="connection water">
          <tbody>
            <tr>
              <td><Label>Connection | Header</Label></td>
              <Empty />
              <td>
                <Sel>{lbl('conn_mtl_id')}</Sel> | <Sel>{lbl('header_mtl_id')}</Sel>
              </td>
            </tr>
            <tr>
              <td><Label>Connections</Label></td>
              <Empty />
              <td>
                <Sel>
                  <span>{lbl('conn_end_id')}</span> | <span>{lbl('conn_hand_id')}</span> |{' '}
                  {s.conn_pipe_end ?? lbl('conn_pipe_end_id')}
                </Sel>
              </td>
            </tr>
            {evaporator && s.distributor_1_dia ? (
              <tr>
                <td><Label>Distributor</Label></td>
                <Empty />
                <td>
                  <span className="print-selection no-wrap">
                    {s.distributor_1_dia}
                    {s.distributor_2_dia ? ` / ${s.distributor_2_dia}` : ''}
                    {s.distributor_3_dia ? ` / ${s.distributor_3_dia}` : ''}
                    {s.distributor_4_dia ? ` / ${s.distributor_4_dia}` : ''}
                  </span>
                </td>
              </tr>
            ) : null}
            <tr>
              <td><Label> In Conn | Header</Label></td>
              <td><Unit>in</Unit></td>
              <td>
                <Sel>
                  <Dec v={perf.inlet_conn_dia} /> | <Dec v={perf.inlet_header_dia} />
                </Sel>
              </td>
            </tr>
            {!evaporator && (
              <tr>
                <td><Label>Out Conn | Header</Label></td>
                <td><Unit>in</Unit></td>
                <td>
                  <Sel>
                    <Dec v={perf.outlet_conn_dia} /> | <Dec v={perf.outlet_header_dia} />
                  </Sel>
                </td>
              </tr>
            )}
            <tr>
              <td><Label>Connection per header</Label></td>
              <Empty />
              {/* legacy seeds `params_coil.connection_per_header` off the
                  coil_params JSON and DEFAULTS it to 1 when absent
                  (coil.selector.js:740) — which is also what the dialog's own
                  select falls back to. The seeded state can hold ''. */}
              <td>
                <Sel>
                  {(s.connection_per_header == null || s.connection_per_header === ''
                    ? 1
                    : s.connection_per_header) as React.ReactNode}
                </Sel>
              </td>
            </tr>
            {fluid && (
              <tr>
                <td><Label>Connection Rotation</Label></td>
                <Empty />
                <td className={String(s.conn_rotation_id ?? '')}>
                  {s.conn_rotation_id == null || String(s.conn_rotation_id) === 'None' ? (
                    <Sel> -- </Sel>
                  ) : (
                    <Sel>{lbl('conn_rotation_id')}</Sel>
                  )}
                </td>
              </tr>
            )}
            {evaporator && s.aux_side_conn ? (
              <tr>
                <td><Label>Aux. Side Connections</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
              </tr>
            ) : null}
            {evaporator && s.nitrogen_charge ? (
              <tr>
                <td><Label>Nitrogen Charge</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
              </tr>
            ) : null}
            {evaporator && s.moisture_eliminator ? (
              <tr>
                <td><Label>Moisture Eliminator</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
              </tr>
            ) : null}
            {/* ★ MIRRORED LEGACY DUPLICATION (tmpl:647-678): the steam
                vacuum-breaker / air-vent pair is emitted TWICE — once as
                "true"/"true" keyed on vacuum_breaker + nitrogen_charge, then
                again as "Yes"/"Yes" keyed on vacuum_breaker +
                thermostatic_vent. A steam coil with both flags prints the row
                twice on dev. Reproduced verbatim; do NOT dedupe without a
                product call. */}
            {steam && s.vacuum_breaker ? (
              <tr>
                <td><Label>Vacuum Breaker</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
              </tr>
            ) : null}
            {steam && s.nitrogen_charge ? (
              <tr>
                <td><Label>Thermostatic Air Vent</Label></td>
                <Empty />
                <td><Sel>true</Sel></td>
              </tr>
            ) : null}
            {steam && s.vacuum_breaker ? (
              <tr>
                <td><Label>Vacuum Breaker</Label></td>
                <Empty />
                <td><Sel>Yes</Sel></td>
              </tr>
            ) : null}
            {steam && s.thermostatic_vent ? (
              <tr>
                <td><Label>Thermostatic Air Vent</Label></td>
                <Empty />
                <td><Sel>Yes</Sel></td>
              </tr>
            ) : null}
            <tr>
              <td><Label>Finish</Label></td>
              <Empty />
              <td><Sel><span>{lbl('fin_finish_id')}</span></Sel></td>
            </tr>
            <tr>
              <td><Label>Weight Dry/Wet / Coil</Label></td>
              <td><Unit>lb</Unit></td>
              <td>
                <Sel>
                  {' '}
                  <Dec v={perf.shipping_weight} /> / <Dec v={perf.operating_weight} />
                </Sel>
              </td>
            </tr>
          </tbody>
        </table>

        {/* tmpl:694-1463 — the drawing. */}
        <table className={`notes ${coilType} notes_${coilType}`}>
          <tbody>
            <tr>
              <td>
                <CoilPrintDrawing
                  coilType={coilType}
                  style={p.style}
                  hand={p.hand}
                  depth={perf.depth}
                  casingHeight={s.casing_height}
                  overallLength={perf.overall_length}
                  fh={s.fh}
                  fl={s.fl}
                  flangeTop={flange('casing_flange_top_id')}
                  flangeHeaderSide={flange('casing_flange_header_side_id')}
                  flangeReturnSide={flange('casing_flange_return_side_id')}
                  flangeBtm={flange('casing_flange_btm_id')}
                  casingLength={p.casingLength}
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* tmpl:1464-1481 */}
        <table className="table-note margin-top-15 warning-msg">
          <tbody>
            {p.warnings.map((w, i) => (
              <tr key={`w${i}`}>
                <td />
                <td><Sel>{w}</Sel></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* tmpl:1482-1489 — the per-manufacturer CMS notes. */}
        <table className="cms-table-note">
          <tbody>
            {p.cmsNotes.map((n, i) => (
              <tr key={`c${i}`}>
                <td />
                <td><Sel>{n}</Sel></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* tmpl:1490-1511 — "Rating Notes :" (the label is a CSS ::before,
            common.css:23493). The vendor's own notes win; otherwise the rated
            row's own notes, and ONLY when there are 2 or more of them. */}
        <table className="rating-notes">
          <tbody>
            {p.apiNotes.length
              ? p.apiNotes.map((n, i) => (
                  <tr className="api-rating-notes" key={`a${i}`}>
                    <td />
                    <td><Sel>{n}</Sel></td>
                  </tr>
                ))
              : p.ratedNotes.length >= 2
              ? p.ratedNotes.map((n, i) => (
                  <tr className="rating-notes" key={`r${i}`}>
                    <td />
                    <td>
                      <Sel>{n} </Sel>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>

        {/* tmpl:1512-1519 */}
        <table className="table-footer">
          <tbody>
            <tr className="print-footer">
              <td>
                <h5 style={{ borderTop: `4px solid rgb${p.reportColor ?? ''}` }}>
                  Powered by Kinetix Air | © {p.year} Kinetix Software LLC | All Rights Reserved
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default CoilPrintSheet;
